import { useEffect, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  authorityOfferFrom,
  createRecord,
  fileAskFromOffer,
  linkRecord,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  transitionRecord,
  updateRecord,
  type LinkRow,
  type RecordRow,
  type RecordsWithLinks,
} from '../lib/api';
import { bumpRecordsVersion, useRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Dispatch.css';

// S3 — THE DISPATCH BOARD (wf04, blessed 2026-08-06): "manual dispatch, made
// fast". Dispatch stays manual in v1 — no predictive allocation, no
// auto-assign. What the surface owes the dispatcher is the two things they
// currently hold in their head: what should be done next, and who is nearest
// and free. Click a job in the queue, then assign it. The board is real; the
// assignment moves.
//
// TWO COLUMNS. Left: "Needs a tech" — every work order the manifest itself
// parks in `unassigned`. Right: "Today" (the tech lanes) above "Where they
// are" (last known, not live tracking).
//
// ---------------------------------------------------------------------
// THE TWO PLACES THIS PORT IS THINNER THAN THE WIREFRAME, AND WHAT THE
// SURFACE SAYS ABOUT IT. Both are named ON SCREEN, not just here: a sort the
// dispatcher cannot see is a sort they will not trust, and the same is true
// of a distance.
//
//  1. NO TRIAGE TAXONOMY. wf04 orders the queue by Alan's own rule — life
//     and safety, then walk-in freezers and coolers, then customer
//     experience. That rule cannot be derived on this port: `priority` on
//     hvac_work_order is deliberately PLAIN TEXT (the archived fork's
//     H1/H2/H3 picklist was invented — see recordTypes.ts's own field note,
//     and a full-column scan of Ether Inc's real workbook found zero H-codes),
//     and no field anywhere says "this is a walk-in freezer". So the queue is
//     ordered by the only two signals a work order actually carries —
//     `urgent`, then how long it has waited — every card NAMES which of the
//     two put it where it is, and the note under the queue says the rule out
//     loud including the part we do not have. Nothing here sniffs the title
//     for the word "freezer": guessing a tier and printing it as fact is the
//     one failure this chunk was told not to commit. The real taxonomy is a
//     question for Alan.
//
//  2. NO SITE COORDINATES. hvac_site carries `service_address` — free text —
//     and nothing else; there is no lat/lon field, no geocoder, no tile
//     provider and no API key. Straight-line distance is therefore not
//     computable, so this board does not draw a plot and does not print
//     kilometres. It answers the same question with what is real: each tech's
//     LAST LOGGED JOB TODAY, and whether that job was at the selected job's
//     own site, at another site of the same client, or somewhere else. That
//     is a proximity signal derived from actual links, it is named on the
//     lane in the words it was derived from, and it is enough to choose
//     between three people — which is all wf04 ever claimed for the map. A
//     schematic plot laid out by hashing record ids would look like
//     arithmetic and be noise; on the one surface whose job is deciding, that
//     is worse than not drawing it.
//
// WHAT ASSIGNMENT ACTUALLY DOES (wf04 §02). Assigning is not a field edit: it
// creates the visit, puts it on the tech's Today, tells the work order who
// has it, and moves the job forward — instantly, with a receipt, because it
// is reversible and internal. Concretely, four writes through the ordinary
// gated record routes:
//     1. createRecord('hvac_visit')                   -> rexx.visit.capture
//     2. linkRecord(visit, 'work_order', wo)          -> rexx.visit.link
//     3. linkRecord(visit, 'tech_profile', tech)      -> rexx.visit.link
//     4. updateRecord(wo, { assigned_handler: name }) -> rexx.work_order.update
//     5. transitionRecord(wo, 'in_progress')          -> rexx.work_order.transition
// Step 4 is the pack's OWN declared assignment field (index.ts's
// comms.topicAssignmentField), and writing it is what makes the server's
// post-commit seam post "Assigned to <name>" into the work order's Telegram
// topic (packages/server/src/plugins/telegramComms.ts's assignmentLine) — the
// tech sees it wherever they are. No bot call is made from here and none ever
// should be; the web writes the record, C5g's server-side notifier does the
// telling. Step 5 fires that seam's transition line the same way. Neither is
// confirmed back to the browser, so the receipt below claims the WRITES it
// made and never claims a delivery it cannot see.
//
// EACH OF THE FIVE IS ITS OWN CASCADE, and that is the honest shape of the
// current client API rather than a design: assignment WANTS to be one
// server-side action with one cascade, one receipt and one reversal. It is
// also why there is no reassign button here — an exchange needs the
// `cascadeId` that POST /records already returns and that lib/api.ts's
// createRecord drops on the floor, and api.ts is not this chunk's file. See
// the reassign note further down.
//
// A refused write is never a dead button: every step reads the authority
// gate's 409 (authorityOfferFrom) and, where the gate minted an offer token,
// files the ask it offered — server-driven, never client-guessed.
//
// NOTHING IN THIS SCREEN SENDS ANYTHING TO A CUSTOMER. Telling a customer a
// time is the one assignment-shaped act that is NOT reversible — the moment
// an appointment window goes outward it enters the always-propose class. In
// v1 nobody tells customers automatically, and the dispatch board is exactly
// where that will stop being theoretical. It is not this chunk.
//
// ---------------------------------------------------------------------
// S16 — THE TWO WAVES ARE GONE, AND SO ARE THE CAPS THAT RATIONED THEM.
//
// S3 built the board's joins as two capped fan-outs because there was no
// bulk link read: one `GET /records/:id?include=links` per visit today
// (TODAY_LINK_CAP 80) and one per work order needing a site (WO_LINK_CAP
// 120) — up to TWO HUNDRED requests for a screen a dispatcher reloads all
// day. On 2026-08-13 that stopped being a latency note and became a
// refusal: the global limiter allows 300 requests per minute per IP, so
// two reloads of this board spent a whole minute's budget and the next
// screen came back `rate_limited` — rendered, everywhere, as "no records".
//
// S15 gave `GET /records` the `?include=links` the single-record door
// always had. Both waves now ride the page they belong to:
//
//   the visits    listAllRecordsWithLinks(hvac_visit,
//                 [tech_profile, work_order])
//   the orders    listAllRecordsWithLinks(hvac_work_order,
//                 [wo_site, wo_client])
//   the names     hvac_tech_profile + hvac_site + hvac_client lists
//
// FIVE reads, one request per 200-row page — about seven for Ether Inc's
// real book (204 orders) — against up to 200 before. The link expansion
// costs nothing per row, so THE CAPS ARE DELETED rather than raised: there
// is no number of visits or work orders this board now refuses to resolve,
// which retires the "this day is bigger than the board can resolve" warning
// as a thing a cap decides.
//
// WHAT REPLACES IT IS NARROWER AND TRUER. The server reports, per record,
// the ids whose links it could not answer for (`unreadable`) or had to cut
// short (`truncated`). A visit in that set is NOT a visit with no tech —
// it is a visit we could not read — and the board says exactly that instead
// of quietly dropping it off a lane. Same for a work order: an unanswered
// order keeps its card and says its location went unread, rather than
// falling back to the work order's own name as if that were where the job
// is.
//
// And the whole board's failure is now the product's own vocabulary
// (lib/readFailure): a 429 says it is temporary and offers to read again.
// A dispatcher who is told "everything has a tech" by a rate limiter will
// stop dispatching, and that is the worst thing this screen could do.

export interface DispatchProps {
  tenantId: string;
  userName: string;
}

type LoadState = 'loading' | 'ready' | 'error';

const WO_TYPE = 'hvac_work_order';
const VISIT_TYPE = 'hvac_visit';
const TECH_TYPE = 'hvac_tech_profile';
const SITE_TYPE = 'hvac_site';
const CLIENT_TYPE = 'hvac_client';

/** The manifest's own word for "needs a tech" — not a guess about what an
 * unassigned job looks like. hvac_work_order's lifecycle opens
 * `intake -> unassigned -> in_progress`, and `intake` is deliberately NOT in
 * the queue: it is the pre-triage tray, one edge before the work is ready to
 * be given to somebody. */
const QUEUE_STATE = 'unassigned';

/** The two relationships each side of the board actually renders, named in
 * the request so the server sends those and nothing else — which is also
 * what keeps a busy record's answer nowhere near the per-record link cap. */
const VISIT_LINK_TYPES = ['tech_profile', 'work_order'];
const WO_LINK_TYPES = ['wo_site', 'wo_client'];

// ---- little pure helpers -------------------------------------------------

function humanize(key: string): string {
  return key.replace(/_/g, ' ');
}

function text(row: RecordRow, field: string): string {
  const v = row.fields[field];
  return typeof v === 'string' ? v.trim() : '';
}

/** How long a job has been waiting, in the wireframe's own register: 40 m,
 * 5 h, 2 d. */
function waited(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (minutes < 60) return `${minutes} m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

function isToday(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** The moment a visit belongs to for "today" purposes. A visit this board
 * just created carries NO time at all (see the assign path on why a time is
 * not invented), so its creation moment is what places it on the day. */
function visitDay(v: RecordRow): string | null {
  const started = v.fields['actual_start'];
  if (typeof started === 'string' && started.length > 0) return started;
  const scheduled = v.fields['scheduled_start'];
  if (typeof scheduled === 'string' && scheduled.length > 0) return scheduled;
  return v.created_at;
}

/** The clock a slot shows, or null when nobody has said when yet. */
function visitTime(v: RecordRow): string | null {
  const started = v.fields['actual_start'];
  if (typeof started === 'string' && started.length > 0) return started;
  const scheduled = v.fields['scheduled_start'];
  if (typeof scheduled === 'string' && scheduled.length > 0) return scheduled;
  return null;
}

/** The other end of a relationship, whichever side of the link row this
 * record sits on. */
function linkedId(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) {
    if (l.link_type !== linkType) continue;
    if (l.src_id === selfId) return l.dst_id;
    if (l.dst_id === selfId) return l.src_id;
  }
  return null;
}

// ---- the shapes the board renders from -----------------------------------

/** Why a card sits where it sits — the SIGNAL, never an inferred tier. */
type QueueWhy = 'urgent' | 'in_turn';

interface QueueJob {
  wo: RecordRow;
  why: QueueWhy;
  siteId: string | null;
  clientId: string | null;
  /** The site's own name — the card's bold line — falling back through the
   * client to the work order itself rather than rendering a blank line. */
  where: string;
  /** S16: whether the server actually answered for this order's links. A
   * card whose links went unread still shows (the JOB is real and still
   * needs a tech) but never lets the work order's own name pass as the
   * place it is at. */
  whereRead: boolean;
  woNumber: string;
  title: string;
  jobType: string;
  /** hvac_work_order.priority, VERBATIM. Free text on this port; shown
   * because it is what the record says, and deliberately not sorted on. */
  priority: string;
}

/** A proximity reading, and the word the lane shows for it. Ranked; a lower
 * rank is nearer. */
type Near = 'same_site' | 'same_client' | 'elsewhere' | 'unknown' | 'unread';

// 'unread' ranks with 'unknown' — never nearest, never starred. It is a
// separate word because "nobody logged anything for them today" and "we
// could not read where they were" are different facts, and a dispatcher
// choosing between three people deserves to know which one they are seeing.
const NEAR_RANK: Record<Near, number> = {
  same_site: 0,
  same_client: 1,
  elsewhere: 2,
  unknown: 3,
  unread: 3,
};
const NEAR_WORD: Record<Near, string> = {
  same_site: 'at this site',
  same_client: 'same client',
  elsewhere: 'another site',
  unknown: 'no position today',
  unread: 'position not read',
};

interface Slot {
  visit: RecordRow;
  /** The work order this visit is for — the slot's line. */
  label: string;
  when: string | null;
  justAdded: boolean;
}

interface Lane {
  tech: RecordRow;
  /** Whatever capability text the record actually carries — `role`, else the
   * tickets it holds. Nothing is matched against it (wf04 §03: capability
   * matching is unmodelled, and that is a data question for Alan before it is
   * a UI one). */
  capability: string;
  slots: Slot[];
  /** Where the last job they logged today was — the honest replacement for a
   * dot on a map. */
  lastSiteId: string | null;
  lastClientId: string | null;
  lastWhere: string | null;
  lastWhen: string | null;
  /** S16: false when the work order behind their last logged job is one the
   * server would not answer links for. Their position is then UNREAD, which
   * is not the same as nothing logged. */
  lastWhereRead: boolean;
}

interface Step {
  state: 'done' | 'asked' | 'refused' | 'held';
  line: string;
}

interface Receipt {
  headline: string;
  steps: Step[];
}

// ---- the screen ----------------------------------------------------------

export function Dispatch({ tenantId, userName }: DispatchProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  // S16: the FAILURE, not a message. A refused board says which refusal it
  // was and, for a 429, that it is temporary and worth reading again.
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [queue, setQueue] = useState<QueueJob[]>([]);
  const [lanes, setLanes] = useState<Lane[]>([]);
  /** How many of today's visits, and how many of the work orders behind
   * them, the SERVER would not answer links for. Not a cap this screen
   * chose — a fact it was told. Normally both zero. */
  const [unread, setUnread] = useState<{ visits: number; orders: number }>({ visits: 0, orders: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyTechId, setBusyTechId] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [freshVisitIds, setFreshVisitIds] = useState<string[]>([]);
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  const loadedOnce = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadBoard() {
      // Only the FIRST load blanks the board. A reload after an assignment
      // must not tear the surface down and take the receipt's context with
      // it — the queue and lanes just become true.
      if (!loadedOnce.current) setLoad('loading');
      setFailure(null);
      try {
        // FIVE reads, whatever the day holds. The two that used to be
        // followed by a fan-out now carry their own links (S15's
        // `?include=links`), naming only the relationships this board
        // renders; the three name lists are unchanged.
        const [orderLinks, visitLinks, techs, sites, clients] = await Promise.all([
          listAllRecordsWithLinks(tenantId, WO_TYPE, { linkTypes: WO_LINK_TYPES }),
          listAllRecordsWithLinks(tenantId, VISIT_TYPE, { linkTypes: VISIT_LINK_TYPES }),
          listAllRecordsByType(tenantId, TECH_TYPE),
          listAllRecordsByType(tenantId, SITE_TYPE),
          listAllRecordsByType(tenantId, CLIENT_TYPE),
        ]);
        if (cancelled) return;

        const workOrders = orderLinks.records;
        const woById = new Map(workOrders.map((w) => [w.id, w]));
        const siteName = new Map(sites.map((s) => [s.id, s.display_name]));
        const clientName = new Map(clients.map((c) => [c.id, c.display_name]));

        // An id the server would not answer for, or whose link set it had to
        // cut short, is one no claim can be made about: a missing wo_site in
        // a truncated set is not evidence the job has no location. These two
        // sets are the whole of what used to be TODAY_LINK_CAP and
        // WO_LINK_CAP, except that the server establishes them rather than
        // this screen choosing them.
        const visitUnanswered = new Set([...visitLinks.unreadable, ...visitLinks.truncated]);
        const woUnanswered = new Set([...orderLinks.unreadable, ...orderLinks.truncated]);
        const woAnswered = (id: string | null): boolean =>
          id !== null && !woUnanswered.has(id) && orderLinks.linksByRecordId[id] !== undefined;

        // Today's field day: every visit whose moment is today. No cap.
        const todaysVisits = visitLinks.records.filter((v) => isToday(visitDay(v)));

        const queueRows = workOrders.filter((w) => w.lifecycle_state === QUEUE_STATE);

        const visitTech = new Map<string, string | null>();
        const visitWo = new Map<string, string | null>();
        for (const v of todaysVisits) {
          const links = visitUnanswered.has(v.id) ? undefined : visitLinks.linksByRecordId[v.id];
          if (links === undefined) {
            visitTech.set(v.id, null);
            visitWo.set(v.id, null);
            continue;
          }
          visitTech.set(v.id, linkedId(links, v.id, 'tech_profile'));
          visitWo.set(v.id, linkedId(links, v.id, 'work_order'));
        }

        const woSite = new Map<string, string | null>();
        const woClient = new Map<string, string | null>();
        for (const w of workOrders) {
          const links = woUnanswered.has(w.id) ? undefined : orderLinks.linksByRecordId[w.id];
          if (links === undefined) continue;
          woSite.set(w.id, linkedId(links, w.id, 'wo_site'));
          woClient.set(w.id, linkedId(links, w.id, 'wo_client'));
        }

        // ---- the queue, ordered by the only two signals that exist -------
        const jobs: QueueJob[] = queueRows.map((wo) => {
          const whereRead = woAnswered(wo.id);
          const siteId = woSite.get(wo.id) ?? null;
          const clientId = woClient.get(wo.id) ?? null;
          const where =
            (siteId ? siteName.get(siteId) : null) ??
            (clientId ? clientName.get(clientId) : null) ??
            wo.display_name;
          return {
            wo,
            why: wo.fields['urgent'] === true ? 'urgent' : 'in_turn',
            siteId,
            clientId,
            where,
            whereRead,
            woNumber: text(wo, 'wo_number'),
            title: text(wo, 'title'),
            jobType: text(wo, 'job_type'),
            priority: text(wo, 'priority'),
          };
        });
        // Flagged urgent first; within each band the one that has waited
        // LONGEST goes first. Both halves are defensible from the record;
        // neither is Alan's rule, and the surface says so.
        jobs.sort((a, b) => {
          const urgency = (a.why === 'urgent' ? 0 : 1) - (b.why === 'urgent' ? 0 : 1);
          if (urgency !== 0) return urgency;
          return a.wo.created_at < b.wo.created_at ? -1 : a.wo.created_at > b.wo.created_at ? 1 : 0;
        });

        // ---- the lanes ---------------------------------------------------
        const nextLanes: Lane[] = techs.map((tech) => {
          const mine = todaysVisits.filter((v) => visitTech.get(v.id) === tech.id);
          const slots: Slot[] = mine
            .map((visit) => {
              const woId = visitWo.get(visit.id) ?? null;
              const job = woId ? woById.get(woId) : undefined;
              return {
                visit,
                label: job?.display_name ?? visit.display_name,
                when: visitTime(visit),
                justAdded: false,
              };
            })
            // Timed slots read in clock order; untimed ones (nobody has said
            // when yet) sit after them rather than pretending to a position.
            .sort((a, b) => {
              if (a.when && b.when) return a.when < b.when ? -1 : a.when > b.when ? 1 : 0;
              if (a.when) return -1;
              if (b.when) return 1;
              return a.visit.created_at < b.visit.created_at ? -1 : 1;
            });

          // Last known: the most recent thing they logged today.
          const newest = [...mine].sort((a, b) => {
            const av = visitDay(a) ?? '';
            const bv = visitDay(b) ?? '';
            return av < bv ? 1 : av > bv ? -1 : 0;
          })[0];
          const lastWoId = newest ? (visitWo.get(newest.id) ?? null) : null;
          const lastSiteId = lastWoId ? (woSite.get(lastWoId) ?? null) : null;
          const lastClientId = lastWoId ? (woClient.get(lastWoId) ?? null) : null;
          // Read = we either have nothing to place them by, or we have an
          // answer. Unread = there IS a last job and the server would not
          // tell us where it was; the lane says so rather than reading as
          // "nothing logged today".
          const lastWhereRead = lastWoId === null || woAnswered(lastWoId);

          const tickets = Array.isArray(tech.fields['tickets'])
            ? (tech.fields['tickets'] as Array<Record<string, unknown>>)
                .map((t) => (typeof t?.['name'] === 'string' ? (t['name'] as string) : ''))
                .filter((n) => n.length > 0)
            : [];

          return {
            tech,
            // The record's own words, in order of how much they say. Never a
            // default, never an invented trade.
            capability: text(tech, 'role') || tickets.join(' · '),
            slots,
            lastSiteId,
            lastClientId,
            lastWhere:
              (lastSiteId ? (siteName.get(lastSiteId) ?? null) : null) ??
              (lastClientId ? (clientName.get(lastClientId) ?? null) : null) ??
              (lastWoId ? (woById.get(lastWoId)?.display_name ?? null) : null),
            lastWhen: newest ? visitDay(newest) : null,
            lastWhereRead,
          };
        });

        setQueue(jobs);
        setLanes(nextLanes);
        setUnread({
          visits: todaysVisits.filter((v) => visitUnanswered.has(v.id)).length,
          orders: [...new Set([...queueRows.map((w) => w.id), ...todaysVisits.map((v) => visitWo.get(v.id))])]
            .filter((id): id is string => typeof id === 'string')
            .filter((id) => !woAnswered(id)).length,
        });
        setSelectedId((current) => (current && jobs.some((j) => j.wo.id === current) ? current : null));
        loadedOnce.current = true;
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
      }
    }

    void loadBoard();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  const selected = queue.find((j) => j.wo.id === selectedId) ?? null;

  /** The proximity reading for one lane against the selected job. Derived
   * from real links only: same site, same client, somewhere else, or nothing
   * logged today. */
  function nearness(lane: Lane): Near {
    if (!selected) return 'unknown';
    // Either end of the comparison being unread makes the reading unread —
    // never 'somewhere else', which would be a claim neither side supports.
    if (!selected.whereRead || !lane.lastWhereRead) return 'unread';
    if (selected.siteId && lane.lastSiteId && selected.siteId === lane.lastSiteId) return 'same_site';
    if (selected.clientId && lane.lastClientId && selected.clientId === lane.lastClientId) {
      return 'same_client';
    }
    if (lane.lastSiteId || lane.lastClientId || lane.lastWhere) return 'elsewhere';
    return 'unknown';
  }

  // The machine does the arithmetic and the human keeps the call: the star
  // marks every lane sharing the nearest reading, and marks NOTHING when the
  // nearest reading is "somewhere else" or "not known" — there is no best
  // option to point at, and pointing anyway would be the fabrication this
  // board refuses.
  const bestRank = selected
    ? Math.min(...lanes.map((l) => NEAR_RANK[nearness(l)]), NEAR_RANK.unknown)
    : NEAR_RANK.unknown;
  const hasBest = selected !== null && bestRank <= NEAR_RANK.same_client;

  /** One gated write, reported as one step. Returns the value on success and
   * null on refusal — a later step's failure never erases an earlier step's
   * truth, which is the whole point of a receipt. */
  async function attempt<T>(line: string, fn: () => Promise<T>, steps: Step[]): Promise<T | null> {
    try {
      const value = await fn();
      steps.push({ state: 'done', line });
      return value;
    } catch (err) {
      const offer = authorityOfferFrom(err);
      if (offer) {
        const token = offer.fileAsk?.available ? offer.fileAsk.offerToken : undefined;
        if (token) {
          try {
            const { askId } = await fileAskFromOffer(tenantId, token);
            steps.push({ state: 'asked', line: `${line} Filed as an ask — ${askId}.` });
            return null;
          } catch {
            steps.push({ state: 'refused', line: `${line} Refused, and the ask could not be filed.` });
            return null;
          }
        }
        steps.push({
          state: 'refused',
          line: offer.missingCapability
            ? `${line} Refused — ${offer.missingCapability} is not yours to use.`
            : `${line} Refused — this is above your authority here.`,
        });
        return null;
      }
      steps.push({
        state: 'refused',
        line: `${line} ${err instanceof ApiError ? err.message : 'The server refused.'}`,
      });
      return null;
    }
  }

  async function assign(lane: Lane) {
    const job = selected;
    if (!job || busyTechId) return;
    setBusyTechId(lane.tech.id);
    setReceipt(null);
    const steps: Step[] = [];
    const techName = lane.tech.display_name;
    const jobName = job.woNumber || job.wo.display_name;

    // NO scheduled_start. The dispatcher chose a person, not a clock, and a
    // time is the one value on this surface that becomes irreversible the
    // moment it is communicated outward (wf04's warning note). The visit goes
    // on the day with no time; the slot says so; the schedule surface is
    // where a time gets set.
    const visit = await attempt(
      `Visit created on ${techName}'s day.`,
      () => createRecord(tenantId, VISIT_TYPE, {}),
      steps
    );

    // No visit, no assignment. The remaining writes would move a job nobody
    // is actually booked onto — so they are not attempted, and the receipt
    // says the job stayed where it was rather than listing four more
    // refusals nobody asked for.
    if (visit) {
      await attempt(`Attached to ${jobName}.`, () => linkRecord(tenantId, visit.id, 'work_order', job.wo.id), steps);
      await attempt(
        `${techName} is on it.`,
        () => linkRecord(tenantId, visit.id, 'tech_profile', lane.tech.id),
        steps
      );

      // The pack's own assignment field. Writing it is what puts "Assigned to
      // <name>" in the job's own thread, server-side.
      await attempt(
        `The job now says ${techName} has it.`,
        () => updateRecord(tenantId, job.wo.id, { assigned_handler: techName }),
        steps
      );

      // Advance the work order. The transition table is the engine's to
      // judge: an illegal edge comes back 422 and is reported, never
      // pre-guessed away.
      await attempt(
        `${jobName} moved on from ${humanize(job.wo.lifecycle_state ?? QUEUE_STATE)}.`,
        () => transitionRecord(tenantId, job.wo.id, 'in_progress'),
        steps
      );
    } else {
      steps.push({ state: 'held', line: `${jobName} stays in the queue.` });
    }

    const landed = steps.filter((s) => s.state === 'done').length;
    setReceipt({
      headline:
        landed === steps.length
          ? `${jobName} is ${techName}'s.`
          : `${jobName} — ${landed} of ${steps.length} landed.`,
      steps,
    });
    if (visit) {
      setFreshVisitIds((ids) => [...ids, visit.id]);
      // The board becomes true immediately, then again from the server.
      setQueue((rows) => rows.filter((r) => r.wo.id !== job.wo.id));
      setSelectedId(null);
    }
    setBusyTechId(null);
    bumpRecordsVersion();
  }

  if (load === 'loading') {
    return (
      <div class="dispatch">
        <div class="dispatch__eyebrow">Dispatch</div>
        <div class="dispatch__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    // NOT an empty board. A dispatcher shown "everything has a tech" by a
    // rate limiter stops dispatching — the headline says what happened, the
    // second line says what is true right now, and a retryable failure gets
    // a door out rather than a reload the dispatcher has to think of.
    return (
      <div class="dispatch">
        <div class="dispatch__eyebrow">Dispatch</div>
        <div class="dispatch__say">
          {failure.headline}
          <br />
          <span class="dispatch__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="dispatch__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-CA', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  return (
    <div class="dispatch">
      <div class="dispatch__topline">
        <b>Dispatch</b>
        <span>·</span>
        <span>{today}</span>
        <span>·</span>
        <span>{userName} dispatching</span>
      </div>

      <div class="dispatch__board">
        {/* ---- LEFT: needs a tech ------------------------------------- */}
        <div class="dispatch__col">
          <div class="dispatch__sec">
            <span>Needs a tech</span>
            <span class="dispatch__mini">{queue.length > 0 ? `${queue.length} waiting` : 'clear'}</span>
          </div>

          {queue.length === 0 ? (
            <div class="dispatch__empty">
              <b>Everything has a tech.</b>
              Nothing is waiting to be assigned.
            </div>
          ) : (
            <div class="dispatch__queue">
              {queue.map((job) => {
                const urgent = job.why === 'urgent';
                return (
                  <button
                    type="button"
                    class="dispatch__card"
                    key={job.wo.id}
                    aria-selected={selectedId === job.wo.id}
                    onClick={() => setSelectedId(selectedId === job.wo.id ? null : job.wo.id)}
                  >
                    <span
                      class="dispatch__stripe"
                      style={{ background: urgent ? 'var(--status-critical)' : 'var(--hair)' }}
                    />
                    <span class="dispatch__card-body">
                      <span class="dispatch__age">{waited(job.wo.created_at)}</span>
                      <span
                        class="dispatch__why"
                        style={{ color: urgent ? 'var(--status-critical)' : 'var(--mute)' }}
                      >
                        {urgent ? 'Flagged urgent' : 'In turn'}
                      </span>
                      <b>{job.where}</b>
                      <small>
                        {[
                          job.woNumber,
                          job.title,
                          job.jobType && job.jobType !== 'unspecified' ? humanize(job.jobType) : '',
                          job.priority ? `priority: ${job.priority}` : '',
                          // The bold line above fell back to the job's own
                          // name; this says so, so nobody reads it as an
                          // address.
                          job.whereRead ? '' : 'location not read',
                        ]
                          .filter((s) => s.length > 0)
                          .join(' · ')}
                      </small>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div class="dispatch__register">
            <b>Flagged urgent, then longest waiting.</b> Those are the two signals a work order carries
            today. Alan's rule — life and safety, then walk-ins and freezers, then customer experience —
            needs a priority taxonomy the record does not have yet.
          </div>
        </div>

        {/* ---- RIGHT: today, then where they are ---------------------- */}
        <div class="dispatch__col">
          <div class="dispatch__sec">
            <span>Today</span>
            <span class="dispatch__mini">
              {selected ? `assigning ${selected.woNumber || selected.wo.display_name}` : 'select a job to assign'}
            </span>
          </div>

          {lanes.length === 0 ? (
            <div class="dispatch__empty">
              <b>No techs on the roster.</b>
              There is nobody to assign to yet.
            </div>
          ) : (
            <div class="dispatch__lanes">
              {lanes.map((lane) => {
                const near = nearness(lane);
                const best = hasBest && NEAR_RANK[near] === bestRank;
                const loadWord = lane.slots.length > 0 ? `${lane.slots.length} today` : 'nothing today';
                return (
                  <div class="dispatch__lane" key={lane.tech.id}>
                    <div class="dispatch__lane-head">
                      <b>{lane.tech.display_name}</b>
                      {lane.capability ? <span class="dispatch__m">{lane.capability}</span> : null}
                      <span class="dispatch__spacer" />
                      <span class="dispatch__m">
                        {selected ? `${NEAR_WORD[near]} · ${loadWord}` : loadWord}
                      </span>
                      <button
                        type="button"
                        class="dispatch__assign"
                        disabled={!selected || busyTechId !== null}
                        onClick={() => void assign(lane)}
                      >
                        {busyTechId === lane.tech.id ? 'assigning…' : best ? '★ assign' : 'assign'}
                      </button>
                    </div>
                    {/* Working patterns are shown, not enforced — and
                        hvac_tech_profile declares no pattern field at all, so
                        nothing is shown rather than a default day invented for
                        someone. Tim starts around eleven; the record does not
                        know that yet. */}
                    <div class="dispatch__slots">
                      {lane.slots.length === 0 ? (
                        <div class="dispatch__slot dispatch__slot--none">
                          <span class="dispatch__slot-t">—</span>
                          <span class="dispatch__slot-b" />
                          <span>Nothing booked today.</span>
                        </div>
                      ) : (
                        lane.slots.map((slot) => (
                          <div
                            class={
                              freshVisitIds.includes(slot.visit.id)
                                ? 'dispatch__slot dispatch__slot--new'
                                : 'dispatch__slot'
                            }
                            key={slot.visit.id}
                          >
                            <span class="dispatch__slot-t">{slot.when ? timeOf(slot.when) : '—'}</span>
                            <span
                              class="dispatch__slot-b"
                              style={{
                                background:
                                  slot.visit.lifecycle_state === 'completed'
                                    ? 'var(--status-good)'
                                    : 'var(--hair)',
                              }}
                            />
                            <span class="dispatch__slot-x">
                              {slot.label}
                              {slot.when ? null : <small> no time yet</small>}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {receipt ? (
            <div class="dispatch__receipt">
              <b>{receipt.headline}</b>
              {receipt.steps.map((step, i) => (
                <span class={`dispatch__step dispatch__step--${step.state}`} key={`${i}-${step.line}`}>
                  {step.line}
                </span>
              ))}
            </div>
          ) : null}

          <div class="dispatch__sec dispatch__sec--later">
            <span>Where they are</span>
            <span class="dispatch__mini">last known · not live tracking</span>
          </div>

          {lanes.length === 0 ? (
            <div class="dispatch__empty">Nobody has logged a job today.</div>
          ) : (
            <div class="dispatch__where">
              {lanes.map((lane) => (
                <div class="dispatch__where-row" key={lane.tech.id}>
                  <span class="dispatch__where-who">{lane.tech.display_name}</span>
                  <span class="dispatch__where-at">
                    {lane.lastWhereRead
                      ? (lane.lastWhere ?? 'nothing logged today')
                      : "couldn't read where they were"}
                  </span>
                  <span class="dispatch__where-t">
                    {lane.lastWhen ? timeOf(lane.lastWhen) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div class="dispatch__register">
            Positions are the last job each tech logged today. <b>Straight-line distance is not shown</b> —
            a site record carries an address, not coordinates, so the board names where somebody was
            rather than drawing how far.
          </div>

          {unread.visits > 0 || unread.orders > 0 ? (
            // S16: no longer "the board gave up at 80" — the SERVER named
            // these. Everything else on the board is complete, which is why
            // this says which rows are affected rather than casting doubt
            // over the whole day.
            <div class="dispatch__register dispatch__register--warn">
              <b>Some of today could not be read.</b>{' '}
              {unread.visits > 0
                ? `${unread.visits} of today's visits ${unread.visits === 1 ? 'has' : 'have'} no readable tech or job`
                : ''}
              {unread.visits > 0 && unread.orders > 0 ? ', and ' : ''}
              {unread.orders > 0
                ? `${unread.orders} work ${unread.orders === 1 ? 'order' : 'orders'} would not say where ${unread.orders === 1 ? 'it is' : 'they are'}`
                : ''}
              . The rest of the board is complete.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// REASSIGN, AND WHY THERE IS NO BUTTON FOR IT YET.
//
// wf04 §02 is explicit: reassigning within the day is an EXCHANGE — same
// value, different referent, re-derived — and never a delete-and-recreate.
// P-A shipped exactly that door (POST /api/cascades/:id/exchange, reached
// through lib/api.ts's exchangeCascade), and it takes a CASCADE ID.
//
// The five writes above each mint a cascade server-side — packages/server/
// src/plugins/records.ts returns `{ record, cascadeId }` from POST /records,
// PATCH /records/:id, POST /records/:id/transition and POST /records/:id/
// links, deliberately and additively. lib/api.ts's createRecord /
// updateRecord / transitionRecord / linkRecord destructure `{ record }` and
// throw the receipt away, and lib/api.ts is not this chunk's file to change
// (the S2/S3/S4 seam contract, 2026-08-10). So the id an exchange needs never
// reaches the browser, and a reassign button built today could only be a
// delete-and-recreate wearing an exchange's clothes.
//
// Two things close it, and they are the same shape:
//   - the small fix: `createRecord` (and its three siblings) return the
//     `cascadeId` the server already sends. One line each, additive, nothing
//     downstream breaks.
//   - the right fix: assignment becomes ONE server-side action — one cascade
//     over all five effects, one receipt, one reversal. Then a reassign is
//     literally an exchange of that cascade with a different tech as the
//     referent, the undo ramp on the receipt is real, and the board stops
//     composing a transaction out of five independent ones that can half-land.
// Until one of them exists this surface offers no reassign, because a button
// that cannot keep wf04's promise is worse than no button.
